import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Lines, SectionTag, EASE } from "../site/Motion";
import { useApp, STOCK_LABELS } from "../../context/AppContext";
import { IMG } from "../../lib/images";

export function Colours() {
  const { colours, colourSlug, setColour, selectedColour } = useApp();
  const active = selectedColour || colours[0];
  const stock = active ? STOCK_LABELS[active.stock] : null;

  const choose = (slug) => {
    setColour(slug);
    const url = new URL(window.location.href);
    url.searchParams.set("colore", slug);
    window.history.replaceState({}, "", url);
  };

  return (
    <section id="colori" data-testid="colours-section" className="relative overflow-hidden border-t border-white/10 bg-ink py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="09" label="Colori" />
        <div className="mt-10 flex flex-wrap items-end justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase leading-[0.92] tracking-tight">
            <Lines lines={["Dress your SWAPA."]} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
          </h2>
          <Reveal delay={0.2}>
            <p className="max-w-xs font-serif text-lg italic text-zinc-400">
              Scegli l'espressione che assomiglia alla tua città.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid items-center gap-12 lg:grid-cols-[1.4fr_1fr]">
          <Reveal y={70}>
            <div className="relative overflow-hidden border border-white/10 bg-card">
              <img
                src={active?.image || IMG.carBase}
                alt={active ? `SWAPA in ${active.name}` : "SWAPA"}
                className={`aspect-[16/10] w-full object-cover ${active?.image ? "" : "grayscale"}`}
                loading="lazy"
              />
              {!active?.image && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active?.slug || "base"}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.85 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="absolute inset-0"
                    style={{ backgroundColor: active?.hex || "#161618", mixBlendMode: "color" }}
                    aria-hidden="true"
                  />
                </AnimatePresence>
              )}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`glow-${active?.slug || "base"}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(to top, ${active?.hex || "#161618"}33, transparent 45%)` }}
                  aria-hidden="true"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute bottom-5 left-5 right-5 flex flex-wrap items-end justify-between gap-3">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={active?.slug || "base-name"}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.45, ease: EASE }}
                    data-testid="colour-active-name"
                    className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl"
                  >
                    {active?.name || "—"}
                  </motion.p>
                </AnimatePresence>
                {stock && (
                  <span data-testid="colour-stock-badge" className={`border bg-black/60 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] backdrop-blur-sm ${stock.tone}`}>
                    {stock.text}
                  </span>
                )}
              </div>
            </div>
          </Reveal>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Quattro espressioni. Una sola SWAPA.</p>
            <div className="mt-6 grid grid-cols-2 gap-4" role="listbox" aria-label="Seleziona colore">
              {colours.map((c, i) => {
                const isActive = active?.slug === c.slug;
                const unavailable = c.stock === "unavailable";
                return (
                  <Reveal key={c.slug} delay={i * 0.08}>
                    <button
                      data-testid={`color-selector-${c.slug}`}
                      onClick={() => !unavailable && choose(c.slug)}
                      disabled={unavailable}
                      role="option"
                      aria-selected={isActive}
                      className={`group flex w-full flex-col items-start gap-3 border p-4 text-left transition-all duration-300 ${
                        isActive ? "border-swapa-yellow bg-white/5" : "border-white/10 hover:border-white/30"
                      } ${unavailable ? "cursor-not-allowed opacity-40" : ""}`}
                    >
                      <span className="h-8 w-8 rounded-full border border-white/20" style={{ backgroundColor: c.hex }} aria-hidden="true" />
                      <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${isActive ? "text-white" : "text-zinc-400 group-hover:text-white"}`}>
                        {c.name}
                      </span>
                    </button>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.3}>
              <Link
                to={`/preordina${active ? `?colore=${active.slug}` : ""}`}
                data-testid="colours-preorder-cta"
                className="group mt-10 inline-flex items-center gap-3 bg-swapa-yellow px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5"
              >
                Pre-ordina in {active?.name || "questo colore"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
